import { FormControl, MenuItem, Stack, TextField, Select, Checkbox, FormControlLabel, InputLabel, FormGroup, Card, Divider, Button, ButtonGroup, Paper, Typography, Tabs, setRef } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGetProductByIdQuery, useUpdateSKUMutation } from '../features/products/productSlice';
import ProductGroupEditor from '../components/ProductGroupEditor';
import TemplateSelectionControl from '../components/TemplateSelectionControl';
import Loading from '../components/Loading';
import { SingleProductTypeSelect } from '../components/ProductAutocomplete';
import ProductMetadataEditor from '../components/ProductMetadataView';
import { useWindowSize } from '../helpers/window';
import TemplateSideTabs from '../components/TemplateSideTabs';
import ProductPhotoView from '../components/ProductPhotoView';
import { useGetSKUImagesQuery } from '../features/model/skuSlice';

const ProductEditView = () => {
    let params = useParams();

    let default_state = {
        "id": 0,
        "name": "",
        "code": "",
        "item_type": "single",
    }

    let [state, setState] = useState(default_state);

    const [refreshIdx, setRefreshIdx] = useState(0);

    const {data: product, error, isLoading } = useGetProductByIdQuery(params.productId)
    const {data: skuimages, error: skuimagesError, isLoading: skuimagesIsLoading} = useGetSKUImagesQuery(product ? product.id : undefined, {skip: !product})

    const [updateSKU, {isLoadingUpdate: isUpdating }] = useUpdateSKUMutation();
    const windowSize = useWindowSize();

    if (isLoading || skuimagesIsLoading || !skuimages) {
        return <Loading />;
    }
    if (error || skuimagesError) {
        return <div>Error: {error}</div>
    }

    let item_types = {
        "single": "Single product",
        "group": "Product group",
        "raw": "Raw product",
        "deactivated": "Deactivated",
        "part": "Part",
        "box": "Packing box",
        "material": "Material"
    }
    const handleChangeItemType = (event) => {
        setState({
            ...state,
            item_type: event.target.value
        });
    };

    if(state.id !== product.id) {
        setState(product);
    }

    const handleSave = (event) => {
        let state_with_ids = {...state,
            box_sku: state.box_sku ? state.box_sku.id : null,
            raw_sku: state.raw_sku ? state.raw_sku.id : null
        }
        updateSKU({ sku_id: state.id, data: state_with_ids })
    }
    const handleRevert = (event) => {
        setState({ ...default_state });
    }

    function triggerRefresh() {
        setRefreshIdx(refreshIdx + 1);
    }

    let product_images = []
    let imgWidth = 400;
    if (windowSize.width > 800) {
        imgWidth = 1200;
    } else if (windowSize.width > 400) {
        imgWidth = 800;
    }

    if(skuimages) {
        product_images.push(<div key="fronttemplate" style={{maxWidth: "300px"}} ><ProductPhotoView width={imgWidth} skuimages={skuimages} key="front" product={product} side="front" purpose="preview" /></div>)
        if(product.front_template !== null) {
            product_images.push(<div key="backtemplate" style={{maxWidth: "300px"}} ><ProductPhotoView width={imgWidth} skuimages={skuimages} key="back" product={product} side="back" purpose="preview_back" /></div>)
        }
    }

    let groupedit = []
    let stockedit = []
    let templateedit = []
    if(state.item_type === "group") {
        groupedit.push(
            <ProductGroupEditor key="group" product={product} />
        )
    } else {
        groupedit.push(
            <ProductGroupEditor key="group" product={product} label="Sideloaded products" />
        )
        stockedit.push(
                <Stack direction="row" spacing={2} key="stock">
                    <TextField label="Stock" defaultValue={state.stock} disabled={state.item_type === "group"} onChange={(event) => {setState({...state, stock: parseInt(event.target.value)})}} />
                    <TextField label="Alert Threshold" defaultValue={state.alert_threshold} onChange={(event) => {setState({...state, alert_threshold: parseInt(event.target.value)})}} />
                    <FormControlLabel control={<Checkbox defaultChecked={state.manual_threshold} onChange={(event) => {setState({...state, manual_threshold: event.target.value === "on"})}} />} label="Manual Threshold" />
                </Stack>
        )
        stockedit.push(
                <Stack direction="row" spacing={2} key="cost">
                    <TextField label="Raw Cost" defaultValue={state.raw_cost} onChange={(event) => {setState({...state, name: event.target.value})}} />
                    <TextField label="Cost of Goods Sold" defaultValue={state.cogs} onChange={(event) => {setState({...state, name: event.target.value})}} />
                </Stack>
        )
        templateedit.push(
            <TemplateSideTabs key={`template_tabs${refreshIdx}`} sku={product} skuimages={skuimages} onChange={(event) => {triggerRefresh()}}/>
        )
    }
    let rawitem = []
    if(product.item_type === "raw") {
        // has a Material SKU
        rawitem.push(
                <SingleProductTypeSelect
                    label="Raw item"
                    item_type="raw"
                    family_id={state.product_family}
                    selection={state.raw_sku ? state.raw_sku.id : null}
                    onSelect={(raw) => {
                        setState({
                            ...state,
                            raw_sku: raw
                        });
                    }}
                />
        )
    }

    return <>
    <Stack sx={{marginTop: 2, marginBottom: 4}} spacing={2}>
        <ButtonGroup>
            <Button component={Link} to={`/products/${product.product_family}`}>Back</Button>
            <Button variant="contained" component={Link} to={`/products/${product.product_family}/${product.id}`}>View</Button>
        </ButtonGroup>

        <h2>{state.name}</h2>
        <TextField label="Name" defaultValue={state.name} onChange={(event) => {setState({...state, name: event.target.value})}} />
        <TextField label="SKU" inputProps={{ style: { fontFamily: "Courier" } }} defaultValue={state.code} onChange={(event) => {setState({...state, code: event.target.value})}} />
        <FormControl>
            <InputLabel shrink>Product Type</InputLabel>
            <Select label="Product Type" id="item_type" value={state.item_type} onChange={handleChangeItemType}>
                {Object.keys(item_types).map((key) =>
                    <MenuItem key={key} value={key}>{item_types[key]}</MenuItem>
                )}
            </Select>
        </FormControl>
        {stockedit}
        <SingleProductTypeSelect
            label="Packing box"
            item_type="box"
            selection={state.box_sku ? state.box_sku.id : null}
            onSelect={(box) => {
                setState({
                    ...state,
                    box_sku: box
                });
            }}
        />
        {rawitem}
        {groupedit}
        <Card><Paper variant="outlined" sx={{padding: 1}}>
            <Typography variant="h7">Product photos</Typography>
        <Stack direction="row" spacing={2} sx={{marginTop: 1}}>
        {product_images}
        </Stack>
        </Paper></Card>
        {templateedit}
        <ProductMetadataEditor product={product} />
        <ButtonGroup>
            <Button onClick={handleRevert}>Revert</Button>
            <Button variant="contained" onClick={handleSave}>Save</Button>
        </ButtonGroup>
    </Stack>
    </>
}

export default ProductEditView;
